import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, use, useState, type ReactNode } from "react";
import type { DataSource } from "@estelle/domain";

import { DataSourceContext } from "@/adapters/data-source";
import { defaultDataSource } from "@/adapters/default-source";

/**
 * Query defaults chosen for an ERP rather than a content site.
 *
 * Nothing here is public or cacheable for long: stock moves, orders arrive,
 * and someone looking at a stale figure will act on it. Refetching when a
 * window regains focus is the cheap version of being current.
 *
 * Retry is off for now. Against fixtures there is nothing to retry, and
 * against the real API a failed read should surface as the error state built
 * in #231 rather than hang for three silent attempts first.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: false,
      },
    },
  });
}

export function DataSourceProvider({
  source,
  children,
}: {
  /**
   * Wins over everything, because tests pass their own. Absent, the provider
   * picks one — see `defaultDataSource`.
   */
  source?: DataSource;
  children: ReactNode;
}) {
  // Created once per mount rather than at module scope: a client shared
  // across tests or across a hot reload keeps a cache nobody asked for.
  const [queryClient] = useState(createQueryClient);
  /*
    Held here, not in `Provide`: a component that suspends before its first
    commit loses its state, so a promise created inside it would be created
    again on every retry and never settle.
  */
  const [fallback] = useState(() => (source ? null : defaultDataSource()));

  return (
    <QueryClientProvider client={queryClient}>
      {/*
        Only ever shown on fixtures, for as long as it takes the fixture chunk
        to arrive; the API adapter is available synchronously.
      */}
      <Suspense fallback={<Booting />}>
        <Provide source={source ?? fallback}>{children}</Provide>
      </Suspense>
    </QueryClientProvider>
  );
}

/**
 * Hands the resolved source to the tree, and nothing renders below it until
 * there is one — so no screen can ask for a data source that has not arrived.
 */
function Provide({
  source,
  children,
}: {
  source: DataSource | Promise<DataSource> | null;
  children: ReactNode;
}) {
  const resolved = source instanceof Promise ? use(source) : source;
  if (!resolved) {
    throw new Error("DataSourceProvider has no data source to provide");
  }

  return <DataSourceContext value={resolved}>{children}</DataSourceContext>;
}

function Booting() {
  return (
    <div
      role="status"
      className="text-muted-foreground flex h-screen items-center justify-center text-sm"
    >
      Loading…
    </div>
  );
}
